import React, { useState, useEffect } from 'react';
import {
	Eye,
	Search,
	RefreshCw,
	ChevronLeft,
	ChevronRight,
	User,
	Calendar,
	Image as ImageIcon,
	CheckCircle,
	Clock,
	AlertCircle,
	ShoppingBag,
	MessageSquare,
	Plus,
	Edit,
	Trash2,
	X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { listingsService, listingRemarksService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const CustomerServiceListings = () => {
	const { user } = useAuth();
	const [listings, setListings] = useState([]);
	const [viewModal, setViewModal] = useState(false);
	const [viewingListing, setViewingListing] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(false);
	const [remarks, setRemarks] = useState([]);
	const [showRemarksModal, setShowRemarksModal] = useState(false);
	const [selectedListingForRemarks, setSelectedListingForRemarks] = useState(null);
	const [newRemark, setNewRemark] = useState('');
	const [remarkType, setRemarkType] = useState('');
	const [addingRemark, setAddingRemark] = useState(false);
	const [loadingRemarks, setLoadingRemarks] = useState(false);
	const [pagination, setPagination] = useState({
		current_page: 1,
		last_page: 1,
		per_page: 15,
		total: 0,
		from: 0,
		to: 0
	});

	// Fetch listings from API
	const fetchListings = async (params = {}) => {
		setLoading(true);
		try {
			// Only include search parameter if search term has at least 3 characters
			const requestParams = {
				page: params.page || pagination.current_page,
				per_page: params.per_page || pagination.per_page,
				sort_by: 'id', // Order by database ID (newest first)
				sort_order: 'desc', // Descending order
				...params
			};

			// Only add search parameter if search term is at least 3 characters
			if (searchTerm && searchTerm.trim().length >= 3) {
				requestParams.search = searchTerm.trim();
			}

			const response = await listingsService.getAll(requestParams);

			if (response.success) {
				setListings(response.data || []);
				if (response.pagination) {
					setPagination(response.pagination);
				} else if (response.meta) {
					// Handle Laravel pagination format
					setPagination({
						current_page: response.meta.current_page || 1,
						last_page: response.meta.last_page || 1,
						per_page: response.meta.per_page || 15,
						total: response.meta.total || 0,
						from: response.meta.from || 0,
						to: response.meta.to || 0
					});
				}
			} else {
				toast.error('Failed to load listings');
			}
		} catch (error) {
			console.error('Error fetching listings:', error);
			toast.error('Error loading listings');
		} finally {
			setLoading(false);
		}
	};

	// Initial load
	useEffect(() => {
		fetchListings();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Reload listings when search term changes (with debounce)
	// Only search when search term has at least 3 characters
	useEffect(() => {
		// If search term is less than 3 characters and not empty, don't search yet
		if (searchTerm.trim().length > 0 && searchTerm.trim().length < 3) {
			// Don't search yet, just wait for more characters
			return;
		}

		// If search is cleared (empty), fetch all listings immediately
		if (searchTerm.trim().length === 0) {
			setPagination(prev => ({ ...prev, current_page: 1 }));
			fetchListings({ page: 1 });
			return;
		}

		// For search terms >= 3 characters, debounce the search
		const timeoutId = setTimeout(() => {
			// Reset to page 1 when searching
			setPagination(prev => ({ ...prev, current_page: 1 }));
			// Fetch with search (will only include search if >= 3 chars)
			fetchListings({ page: 1 });
		}, 500);

		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm]);

	// Reload when pagination changes (but not when search term changes)
	useEffect(() => {
		// Only fetch if search term is empty or has at least 3 characters
		if (searchTerm.trim().length === 0 || searchTerm.trim().length >= 3) {
			fetchListings();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.current_page, pagination.per_page]);

	const getStatusColor = (status) => {
		const statusMap = {
			'pending': '#F59E0B',
			'listed': '#10B981',
			'in-process': '#3B82F6',
			'in-transit': '#8B5CF6',
			'at-store': '#6366F1',
			'authenticated': '#10B981',
			'rejected': '#EF4444',
			'disposed': '#6B7280',
			'returned-to-seller': '#10B981',
			'de-listed': '#6B7280',
			// Numeric statuses
			0: '#F59E0B',
			1: '#F59E0B',
			2: '#10B981',
			3: '#8B5CF6',
			4: '#6366F1',
			5: '#10B981',
			6: '#EF4444',
			7: '#6B7280',
			8: '#10B981',
			9: '#6B7280'
		};
		return statusMap[status] || '#6B7280';
	};

	const getStatusIcon = (status) => {
		const iconMap = {
			'pending': Clock,
			'listed': CheckCircle,
			'in-process': Clock,
			'in-transit': Clock,
			'at-store': ShoppingBag,
			'authenticated': CheckCircle,
			'rejected': AlertCircle,
			'disposed': AlertCircle,
			'returned-to-seller': CheckCircle,
			'de-listed': AlertCircle,
			// Numeric statuses
			0: Clock,
			1: Clock,
			2: CheckCircle,
			3: Clock,
			4: ShoppingBag,
			5: CheckCircle,
			6: AlertCircle,
			7: AlertCircle,
			8: CheckCircle,
			9: AlertCircle
		};
		return iconMap[status] || AlertCircle;
	};

	const getStatusText = (listing) => {
		if (listing.status_text) {
			return listing.status_text;
		}
		if (listing.dress_status) {
			return listing.dress_status.name || listing.dress_status.slug || 'Unknown';
		}
		if (typeof listing.status === 'string') {
			return listing.status.charAt(0).toUpperCase() + listing.status.slice(1).replace(/-/g, ' ');
		}
		return 'Pending';
	};

	const openViewModal = async (listing) => {
		try {
			setLoading(true);
			const response = await listingsService.getById(listing.listing_id);
			
			if (response.success) {
				setViewingListing(response.data);
				setViewModal(true);
			} else {
				toast.error('Failed to fetch listing details');
			}
		} catch (error) {
			console.error('Error fetching listing details:', error);
			toast.error('Error fetching listing details');
		} finally {
			setLoading(false);
		}
	};

	const closeViewModal = () => {
		setViewModal(false);
		setViewingListing(null);
	};

	const openRemarksModal = async (listing) => {
		setSelectedListingForRemarks(listing);
		setShowRemarksModal(true);
		setNewRemark('');
		setRemarkType('');
		await loadRemarks(listing.listing_id);
	};

	const closeRemarksModal = () => {
		setShowRemarksModal(false);
		setSelectedListingForRemarks(null);
		setRemarks([]);
		setNewRemark('');
		setRemarkType('');
	};

	const loadRemarks = async (listingId) => {
		try {
			setLoadingRemarks(true);
			const response = await listingRemarksService.getAll(listingId);
			if (response.success) {
				setRemarks(response.data || []);
			}
		} catch (error) {
			console.error('Error loading remarks:', error);
			toast.error('Failed to load remarks');
		} finally {
			setLoadingRemarks(false);
		}
	};

	const addRemark = async () => {
		if (!newRemark.trim()) {
			toast.error('Please enter a remark');
			return;
		}

		try {
			setAddingRemark(true);
			const response = await listingRemarksService.create({
				listing_id: selectedListingForRemarks.listing_id,
				remark: newRemark.trim(),
				remark_type: remarkType || null
			});

			if (response.success) {
				toast.success('Remark added successfully');
				setNewRemark('');
				setRemarkType('');
				await loadRemarks(selectedListingForRemarks.listing_id);
				// Update remarks count in listings
				fetchListings();
			}
		} catch (error) {
			console.error('Error adding remark:', error);
			toast.error('Failed to add remark');
		} finally {
			setAddingRemark(false);
		}
	};

	const deleteRemark = async (remarkId) => {
		if (!window.confirm('Are you sure you want to delete this remark?')) {
			return;
		}

		try {
			await listingRemarksService.delete(remarkId);
			toast.success('Remark deleted successfully');
			await loadRemarks(selectedListingForRemarks.listing_id);
			fetchListings();
		} catch (error) {
			console.error('Error deleting remark:', error);
			toast.error('Failed to delete remark');
		}
	};

	// Preset remark types for quick selection
	const presetRemarkTypes = [
		'Customer call for inquiry',
		'Customer need this urgently',
		'Customer calls for package status',
		'Customer requested update',
		'Customer complaint',
		'Follow up required',
		'Special handling needed'
	];

	const handlePageChange = (page) => {
		setPagination(prev => ({ ...prev, current_page: page }));
	};

	const handlePerPageChange = (e) => {
		const perPage = parseInt(e.target.value);
		setPagination(prev => ({ ...prev, per_page: perPage, current_page: 1 }));
	};

	return (
		<div>
			<div className="card">
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px'
				}}>
					<div>
						<h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
							All Listings
						</h1>
						<p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '14px' }}>
							View all listings with their current status
						</p>
					</div>
					<button
						onClick={() => fetchListings()}
						className="btn btn-secondary"
						disabled={loading}
						title="Refresh Listings"
					>
						<RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
						Refresh
					</button>
				</div>

				{/* Search */}
				<div style={{ marginBottom: '20px' }}>
					<div style={{ position: 'relative', maxWidth: '400px', marginBottom: '8px' }}>
						<Search
							size={18}
							style={{
								position: 'absolute',
								left: '12px',
								top: '50%',
								transform: 'translateY(-50%)',
								color: '#6b7280'
							}}
						/>
						<input
							type="text"
							placeholder="Search by listing ID, brand, category... (min 3 characters)"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="search-box"
							style={{ paddingLeft: '40px' }}
						/>
					</div>
					{searchTerm.trim().length > 0 && searchTerm.trim().length < 3 && (
						<div style={{
							fontSize: '12px',
							color: '#6b7280',
							marginTop: '4px',
							fontStyle: 'italic'
						}}>
							Type at least 3 characters to search the database
						</div>
					)}
					{searchTerm.trim().length >= 3 && (
						<div style={{
							fontSize: '12px',
							color: '#10b981',
							marginTop: '4px'
						}}>
							Searching database for: "{searchTerm}"
						</div>
					)}
				</div>

				{/* Table */}
				<div style={{ overflowX: 'auto' }}>
					<table className="table">
						<thead>
							<tr>
								<th>Image</th>
								<th>Listing ID</th>
								<th>Seller</th>
								<th>Status</th>
								<th>Created Date</th>
								<th>Remarks</th>
								<th style={{ width: '120px' }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{loading && listings.length === 0 ? (
								<tr>
									<td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
										<div style={{ color: '#6b7280' }}>Loading listings...</div>
									</td>
								</tr>
							) : listings.length === 0 ? (
								<tr>
									<td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
										<div style={{ color: '#6b7280' }}>No listings found</div>
									</td>
								</tr>
							) : (
								listings.map((listing) => {
									const StatusIcon = getStatusIcon(listing.status || listing.dress_status_id || 'pending');
									const statusText = getStatusText(listing);
									
									return (
										<tr key={listing.listing_id}>
											{/* Image */}
											<td>
												<div style={{
													width: '60px',
													height: '60px',
													backgroundColor: '#f3f4f6',
													borderRadius: '8px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													overflow: 'hidden',
													position: 'relative'
												}}>
													{listing.front_image ? (
														<img
															src={listing.front_image}
															alt="Listing"
															style={{
																width: '100%',
																height: '100%',
																objectFit: 'cover'
															}}
														/>
													) : (
														<ImageIcon size={24} color="#9ca3af" />
													)}
												</div>
											</td>

											{/* Listing ID */}
											<td>
												<div style={{
													fontWeight: '600',
													color: '#111827',
													fontSize: '14px'
												}}>
													{listing.listing_id}
												</div>
											</td>

											{/* Seller */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px'
												}}>
													<User size={14} color="#6b7280" />
													<span style={{ fontSize: '14px' }}>
														{listing.username || listing.seller_name || 'N/A'}
													</span>
												</div>
											</td>

											{/* Status */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px',
													backgroundColor: getStatusColor(listing.status || listing.dress_status_id || 'pending'),
													color: 'white',
													padding: '4px 8px',
													borderRadius: '12px',
													fontSize: '12px',
													fontWeight: '500',
													width: 'fit-content'
												}}>
													<StatusIcon size={12} />
													{statusText}
												</div>
											</td>

											{/* Created Date */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px'
												}}>
													<Calendar size={12} color="#6b7280" />
													<span style={{ fontSize: '12px', color: '#6b7280' }}>
														{listing.created_date || (listing.created_at ? new Date(listing.created_at).toLocaleDateString() : 'N/A')}
													</span>
												</div>
											</td>

											{/* Remarks */}
											<td>
												<button
													onClick={() => openRemarksModal(listing)}
													className="btn btn-secondary"
													style={{
														padding: '6px 12px',
														fontSize: '12px',
														display: 'flex',
														alignItems: 'center',
														gap: '6px'
													}}
													title="Manage Remarks"
												>
													<MessageSquare size={14} />
													Remarks
													{(listing.remarks_count || 0) > 0 && (
														<span style={{
															backgroundColor: '#ef4444',
															color: 'white',
															borderRadius: '10px',
															padding: '2px 6px',
															fontSize: '10px',
															fontWeight: '600',
															marginLeft: '4px'
														}}>
															{listing.remarks_count || 0}
														</span>
													)}
												</button>
											</td>

											{/* Actions */}
											<td>
												<div style={{ display: 'flex', gap: '6px' }}>
													<button
														onClick={() => openViewModal(listing)}
														className="btn btn-secondary"
														style={{ padding: '6px 8px', fontSize: '12px' }}
														title="View Details"
													>
														<Eye size={14} />
													</button>
												</div>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>

				{/* Pagination */}
				{pagination.total > 0 && (
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: '24px',
						paddingTop: '24px',
						borderTop: '1px solid #e5e7eb'
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
							<span style={{ fontSize: '14px', color: '#6b7280' }}>
								Showing {pagination.from} to {pagination.to} of {pagination.total} listings
							</span>
							<select
								value={pagination.per_page}
								onChange={handlePerPageChange}
								style={{
									padding: '6px 12px',
									border: '1px solid #d1d5db',
									borderRadius: '6px',
									fontSize: '14px'
								}}
							>
								<option value={15}>15 per page</option>
								<option value={25}>25 per page</option>
								<option value={50}>50 per page</option>
								<option value={100}>100 per page</option>
							</select>
						</div>

						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<button
								onClick={() => handlePageChange(pagination.current_page - 1)}
								disabled={pagination.current_page === 1 || loading}
								className="btn btn-secondary"
								style={{ padding: '6px 12px' }}
							>
								<ChevronLeft size={16} />
								Previous
							</button>
							
							<span style={{ fontSize: '14px', color: '#6b7280', padding: '0 12px' }}>
								Page {pagination.current_page} of {pagination.last_page}
							</span>
							
							<button
								onClick={() => handlePageChange(pagination.current_page + 1)}
								disabled={pagination.current_page === pagination.last_page || loading}
								className="btn btn-secondary"
								style={{ padding: '6px 12px' }}
							>
								Next
								<ChevronRight size={16} />
							</button>
						</div>
					</div>
				)}
			</div>

			{/* View Modal */}
			{viewModal && viewingListing && (
				<div className="modal-overlay" onClick={closeViewModal}>
					<div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
						<div className="modal-header">
							<h2 className="modal-title">Listing Details - {viewingListing.listing_id}</h2>
							<button onClick={closeViewModal} className="close-btn">×</button>
						</div>
						<div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
								<div>
									<strong>Listing ID:</strong> {viewingListing.listing_id}
								</div>
								<div>
									<strong>Status:</strong> {getStatusText(viewingListing)}
								</div>
								<div>
									<strong>Seller:</strong> {viewingListing.username || viewingListing.seller_name || 'N/A'}
								</div>
								<div>
									<strong>Created:</strong> {viewingListing.created_date || (viewingListing.created_at ? new Date(viewingListing.created_at).toLocaleDateString() : 'N/A')}
								</div>
							</div>
							{viewingListing.designer_brand && (
								<div style={{ marginBottom: '20px' }}>
									<strong>Designer/Brand:</strong> {viewingListing.designer_brand}
								</div>
							)}
							{viewingListing.category && (
								<div style={{ marginBottom: '20px' }}>
									<strong>Category:</strong> {viewingListing.category}
								</div>
							)}
							{viewingListing.defects && viewingListing.defects.length > 0 && (
								<div style={{ marginBottom: '20px' }}>
									<strong>Defects:</strong>
									<ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
										{viewingListing.defects.map((defect, index) => (
											<li key={index}>{defect}</li>
										))}
									</ul>
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Remarks Modal */}
			{showRemarksModal && selectedListingForRemarks && (
				<div className="modal-overlay" onClick={closeRemarksModal}>
					<div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%' }}>
						<div className="modal-header">
							<h2 className="modal-title">
								Remarks - {selectedListingForRemarks.listing_id}
							</h2>
							<button onClick={closeRemarksModal} className="close-btn">×</button>
						</div>
						<div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
							{/* Add New Remark Section */}
							<div style={{
								backgroundColor: '#f9fafb',
								border: '1px solid #e5e7eb',
								borderRadius: '8px',
								padding: '20px',
								marginBottom: '24px'
							}}>
								<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
									Add New Remark
								</h3>
								
								{/* Preset Remark Types */}
								<div style={{ marginBottom: '12px' }}>
									<label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block', color: '#374151' }}>
										Quick Select (Optional):
									</label>
									<div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
										{presetRemarkTypes.map((type) => (
											<button
												key={type}
												type="button"
												onClick={() => setRemarkType(type)}
												style={{
													padding: '6px 12px',
													borderRadius: '6px',
													border: remarkType === type ? '2px solid #3b82f6' : '1px solid #d1d5db',
													backgroundColor: remarkType === type ? '#eff6ff' : 'white',
													color: '#111827',
													fontSize: '12px',
													cursor: 'pointer',
													transition: 'all 0.2s'
												}}
											>
												{type}
											</button>
										))}
									</div>
								</div>

								{/* Custom Remark Type */}
								<div style={{ marginBottom: '12px' }}>
									<label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px', display: 'block', color: '#374151' }}>
										Remark Type (Optional):
									</label>
									<input
										type="text"
										value={remarkType}
										onChange={(e) => setRemarkType(e.target.value)}
										placeholder="e.g., Customer inquiry, Package status, etc."
										style={{
											width: '100%',
											padding: '8px 12px',
											border: '1px solid #d1d5db',
											borderRadius: '6px',
											fontSize: '14px'
										}}
									/>
								</div>

								{/* Remark Text */}
								<div style={{ marginBottom: '12px' }}>
									<label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px', display: 'block', color: '#374151' }}>
										Remark: <span style={{ color: '#ef4444' }}>*</span>
									</label>
									<textarea
										value={newRemark}
										onChange={(e) => setNewRemark(e.target.value)}
										placeholder="Enter your remark here..."
										rows={4}
										style={{
											width: '100%',
											padding: '10px 12px',
											border: '1px solid #d1d5db',
											borderRadius: '6px',
											fontSize: '14px',
											resize: 'vertical',
											fontFamily: 'inherit'
										}}
									/>
								</div>

								<button
									onClick={addRemark}
									disabled={addingRemark || !newRemark.trim()}
									className="btn btn-primary"
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
										width: '100%',
										justifyContent: 'center'
									}}
								>
									<Plus size={16} />
									{addingRemark ? 'Adding...' : 'Add Remark'}
								</button>
							</div>

							{/* Existing Remarks */}
							<div>
								<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
									Existing Remarks ({remarks.length})
								</h3>
								
								{loadingRemarks ? (
									<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
										Loading remarks...
									</div>
								) : remarks.length === 0 ? (
									<div style={{
										textAlign: 'center',
										padding: '40px',
										backgroundColor: '#f9fafb',
										borderRadius: '8px',
										color: '#6b7280'
									}}>
										<MessageSquare size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
										<p>No remarks added yet</p>
									</div>
								) : (
									<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
										{remarks.map((remark) => (
											<div
												key={remark.id}
												style={{
													backgroundColor: 'white',
													border: '1px solid #e5e7eb',
													borderRadius: '8px',
													padding: '16px',
													position: 'relative'
												}}
											>
												<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
													<div style={{ flex: 1 }}>
														{remark.remark_type && (
															<span style={{
																display: 'inline-block',
																padding: '4px 8px',
																backgroundColor: '#eff6ff',
																color: '#2563eb',
																borderRadius: '4px',
																fontSize: '11px',
																fontWeight: '600',
																marginBottom: '8px'
															}}>
																{remark.remark_type}
															</span>
														)}
														<p style={{
															margin: '8px 0 0',
															color: '#374151',
															fontSize: '14px',
															lineHeight: '1.5',
															whiteSpace: 'pre-wrap'
														}}>
															{remark.remark}
														</p>
													</div>
													{remark.user_id === user?.id && (
														<button
															onClick={() => deleteRemark(remark.id)}
															style={{
																padding: '4px 8px',
																backgroundColor: '#fee2e2',
																color: '#dc2626',
																border: 'none',
																borderRadius: '4px',
																cursor: 'pointer',
																display: 'flex',
																alignItems: 'center',
																gap: '4px',
																fontSize: '12px'
															}}
															title="Delete Remark"
														>
															<Trash2 size={14} />
														</button>
													)}
												</div>
												<div style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													marginTop: '12px',
													paddingTop: '12px',
													borderTop: '1px solid #f3f4f6',
													fontSize: '12px',
													color: '#6b7280'
												}}>
													<span>
														By: {remark.user?.name || 'Unknown'} ({remark.user?.email || 'N/A'})
													</span>
													<span>
														{new Date(remark.created_at).toLocaleString()}
													</span>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CustomerServiceListings;

